import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@saleor/components/Checkbox";
import Money from "@saleor/components/Money";
import Percent from "@saleor/components/Percent";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { VoucherListUrlSortField } from "@saleor/discounts/urls";
import { maybe, renderCollection, splitDateTime } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { DiscountValueTypeEnum } from "@saleor/types/globalTypes";
import { getArrowDirection } from "@saleor/utils/sort";
import { getFooterColSpanWithBulkActions } from "@saleor/utils/tables";
import React from "react";
import { FormattedMessage } from "react-intl";

import { VoucherList_vouchers_edges_node } from "../../types/VoucherList";

export interface VoucherListProps
  extends ListProps,
    ListActions,
    SortPage<VoucherListUrlSortField> {
  defaultCurrency: string;
  vouchers: VoucherList_vouchers_edges_node[];
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colMinSpent: {
        width: 100
      },
      colName: {
        width: 250,
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colOrder: {
        width: 100
      },
      colCode: {
        width: 200,
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colStart: {
        width: 170,
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colUses: {
        textAlign: "center"
      },
      colValue: {
        width: 125
      }
    },
    colMinSpent: {
      textAlign: "left"
    },
    colName: {
      paddingLeft: 0
    },
    colStart: {
      textAlign: "center"
    },
    colUses: {
      textAlign: "right"
    },
    colValue: {
      textAlign: "right"
    },
    tableRow: {
      cursor: "pointer"
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "VoucherList" }
);

const numberOfColumns = 9;

const VoucherList: React.FC<VoucherListProps> = props => {
  const {
    settings,
    disabled,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort,
    pageInfo,
    vouchers,
    isChecked,
    selected,
    sort,
    toggle,
    toggleAll,
    toolbar,
    defaultCurrency
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns + 1}
        selected={selected}
        disabled={disabled}
        items={vouchers}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(VoucherListUrlSortField.name)}
          className={classes.colName}
        >
          <FormattedMessage defaultMessage="Name" description="voucher Name" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.code
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(VoucherListUrlSortField.code)}
          className={classes.colCode}
        >
          <FormattedMessage defaultMessage="Code" description="voucher Code" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.minSpent
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(VoucherListUrlSortField.minSpent)}
          className={classes.colMinSpent}
        >
          <FormattedMessage
            defaultMessage="Min.Spent"
            description="Min.Spent"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.displayOrder
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(VoucherListUrlSortField.displayOrder)}
          className={classes.colOrder}
        >
          <FormattedMessage defaultMessage="Order" description="displayOrder" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.open
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(VoucherListUrlSortField.open)}
          className={classes.colOrder}
        >
          <FormattedMessage
            defaultMessage="Public"
            description="Is it a public ticket"
          />
        </TableCellHeader>

        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.startDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(VoucherListUrlSortField.startDate)}
          className={classes.colStart}
        >
          <FormattedMessage
            defaultMessage="Starts"
            description="voucher is active from date"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.endDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(VoucherListUrlSortField.endDate)}
          className={classes.colStart}
        >
          <FormattedMessage
            defaultMessage="Ends"
            description="voucher is active until date"
          />
        </TableCellHeader>

        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.value
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.value)}
          className={classes.colValue}
        >
          <FormattedMessage
            defaultMessage="Value"
            description="voucher value"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.limit
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.limit)}
          className={classes.colUses}
        >
          <FormattedMessage defaultMessage="Uses" description="voucher uses" />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={getFooterColSpanWithBulkActions(vouchers, numberOfColumns)}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          vouchers,
          voucher => {
            const isSelected = voucher ? isChecked(voucher.id) : false;

            return (
              <TableRow
                className={!!voucher ? classes.tableRow : undefined}
                hover={!!voucher}
                key={voucher ? voucher.id : "skeleton"}
                selected={isSelected}
                onClick={voucher ? onRowClick(voucher.id) : undefined}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(voucher.id)}
                  />
                </TableCell>
                <TableCell className={classes.colName}>
                  {maybe<React.ReactNode>(() => voucher.name, <Skeleton />)}
                </TableCell>
                <TableCell className={classes.colCode}>
                  {maybe<React.ReactNode>(() => voucher.code, <Skeleton />)}
                </TableCell>
                <TableCell className={classes.colMinSpent}>
                  {voucher && voucher.minSpent ? (
                    <Money money={voucher.minSpent} />
                  ) : voucher && voucher.minSpent === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  style={{ textAlign: "center" }}
                  className={classes.colOrder}
                >
                  {voucher ? voucher.displayOrder : <Skeleton />}
                </TableCell>
                <TableCell
                  style={{ textAlign: "center" }}
                  className={classes.colOrder}
                >
                  {voucher ? voucher.applyShopAll ? "Yes" : "No" : <Skeleton />}
                </TableCell>

                <TableCell className={classes.colStart}>
                  {voucher && voucher.startDate ? (
                    splitDateTime(maybe(() => voucher.startDate, "")).date +
                    " " +
                    splitDateTime(maybe(() => voucher.startDate, "")).time
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colStart}>
                  {voucher && voucher.endDate ? (
                    splitDateTime(maybe(() => voucher.endDate, "")).date +
                    " " +
                    splitDateTime(maybe(() => voucher.endDate, "")).time
                  ) : voucher && voucher.endDate === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colValue}
                  onClick={voucher ? onRowClick(voucher.id) : undefined}
                >
                  {voucher &&
                  voucher.discountValueType &&
                  voucher.discountValue ? (
                    voucher.discountValueType ===
                    DiscountValueTypeEnum.FIXED ? (
                      <Money
                        money={{
                          amount: voucher.discountValue,
                          currency: defaultCurrency
                        }}
                      />
                    ) : (
                      <Percent amount={voucher.discountValue} />
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colUses}>
                  {maybe<React.ReactNode>(
                    () =>
                      voucher.usageLimit === null ? "-" : voucher.usageLimit,
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No vouchers found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
VoucherList.displayName = "VoucherList";
export default VoucherList;
